import { ComponentProps, ReactNode } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useUniqueId } from '~/utils/compute';
import Icon from './Icon';

enum Variants {
  Info = 'info',
  Success = 'success',
  Error = 'error',
}

type VariantStyle = {
  bg: string;
  border: string;
  content: string;
  icon: string;
  title: string;
};

const variantStyles: Record<Variants, VariantStyle> = {
  [Variants.Info]: {
    bg: 'bg-surface-container-low',
    border: 'border-outline-variant',
    content: 'text-on-surface-variant',
    icon: 'text-on-surface-variant',
    title: 'text-on-surface',
  },
  [Variants.Success]: {
    bg: 'bg-emphasis-container',
    border: 'border-emphasis',
    content: 'text-on-emphasis-container',
    icon: 'text-on-emphasis-container',
    title: 'text-on-emphasis-container',
  },
  [Variants.Error]: {
    bg: 'bg-critical-container',
    border: 'border-critical',
    content: 'text-on-critical-container',
    icon: 'text-on-critical-container',
    title: 'text-on-critical-container',
  },
};

const iconStyles = 'size-5';
const icons: Record<Variants, ComponentProps<typeof Icon>['icon']> = {
  [Variants.Info]: 'InfoCircleSolid',
  [Variants.Success]: 'CheckCircleSolid',
  [Variants.Error]: 'ExclamationCircleSolid',
};

type Props = {
  title: string;
  children?: ReactNode;
  className?: string;
  variant?: `${Variants}`;
};
export default function Alert({
  title,
  children,
  className,
  variant = Variants.Error,
}: Props) {
  const id = useUniqueId();

  return (
    <div
      className={twMerge(
        'rounded-md border p-4',
        variantStyles[variant].bg,
        variantStyles[variant].border,
        className,
      )}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      aria-labelledby={id}
    >
      <div className="grid grid-cols-[auto_1fr] items-start gap-x-3">
        <Icon
          icon={icons[variant]}
          className={twJoin(iconStyles, variantStyles[variant].icon)}
        />
        <div>
          <div
            id={id}
            className={twJoin(
              'subheading-sm wrap-anywhere',
              variantStyles[variant].title,
            )}
          >
            {title}
          </div>
          {children && (
            <div
              className={twJoin(
                'mt-2 body-md wrap-anywhere',
                variantStyles[variant].content,
              )}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
